import { Award, Drama, Medal as MedalIcon, Star, Trophy } from 'lucide-react'
import { RevealCurtain, useRevealCountdown } from './RevealCountdown'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import { CEREMONY_SUBTITLE, EVENT_FOOTER, StageChrome, type StagePalette } from './StageChrome'
import type { AwardView } from '../admin/screenTypes'

/**
 * A judged award on the stage screen — slides 25 and 26.
 *
 * Two shapes: a podium of 1st/2nd/3rd for the two booth awards, and a single
 * spotlight for Best Cultural Performance. Best Traditional Attire has its own
 * screen (`AttireScreen`) because its subject is a person, not a booth.
 *
 * The layout is the Committee's approved one: a card per place with its medal
 * astride the top edge, standing on a numbered block, 2nd left, 1st raised in
 * the middle, 3rd right. Header and footer are the lucky draw's, through
 * `StageChrome`.
 *
 * Places arrive one at a time and in reveal order (3rd, then 2nd, then 1st),
 * because the server withholds the rest — so an unrevealed card genuinely has
 * nothing to leak, not even in the payload behind it.
 */

/**
 * A ground per award, as the approved layouts have it: blue for Best SDG
 * Project, teal for Best Sustainable Booth Practice, crimson for Best
 * Cultural Performance. Slide 16's "each has its own colour theme" applies to
 * the awards as much as to the draws — it is how the hall can tell, from the
 * back, that a different award has started.
 */
const PALETTES: Record<string, StagePalette> = {
  sdg_project: {
    background:
      'radial-gradient(ellipse at 50% 42%, rgba(170,215,255,0.16), transparent 58%), repeating-conic-gradient(from 212deg at 50% 44%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(120deg, #19518C 0%, #2C6FA8 38%, #15306B 100%)',
    border: '#0C1E44',
    sparkle: '#F0B84D',
    sparkleAlt: '#8FC7E3',
  },
  sustainable_booth: {
    background:
      'radial-gradient(ellipse at 50% 42%, rgba(150,235,215,0.16), transparent 58%), repeating-conic-gradient(from 212deg at 50% 44%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(135deg, #0E4A43 0%, #15726A 46%, #0B3A3F 100%)',
    border: '#07282B',
    sparkle: '#F0B84D',
    sparkleAlt: '#8FE3D2',
  },
  /*
   * Best Entrepreneurship Booth — amber, the one warm ground among the
   * booth awards. It follows the two blue-green ones on the programme, and
   * the point of a palette per award is that the back of the hall can see the
   * ceremony has moved on without reading a word of it.
   */
  entrepreneurship: {
    background:
      'radial-gradient(ellipse at 50% 42%, rgba(255,222,170,0.17), transparent 58%), repeating-conic-gradient(from 212deg at 50% 44%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(125deg, #7A3B12 0%, #A8641C 42%, #4A2309 100%)',
    border: '#2E1405',
    sparkle: '#FFD98A',
    sparkleAlt: '#F2A65A',
  },
  cultural_performance: {
    background:
      'radial-gradient(ellipse at 50% 38%, rgba(255,196,214,0.18), transparent 56%), repeating-conic-gradient(from 208deg at 50% 40%, rgba(255,255,255,0.055) 0deg 6deg, transparent 6deg 17deg), linear-gradient(120deg, #8A2046 0%, #9C2A5C 38%, #51195A 100%)',
    border: '#33103A',
    sparkle: '#FFD16A',
    sparkleAlt: '#F3A8C8',
  },
}

/** An award the deck gains later still gets a stage, not a blank one. */
const FALLBACK_PALETTE = PALETTES.sdg_project

/** Confetti as still specks; the moving kind fires when 1st place lands. */
const SPECKS = [
  { left: '6%', top: '18%', color: '#F4B63F', size: '0.8vmin', spin: '18deg' },
  { left: '19%', top: '32%', color: '#6FD3E0', size: '0.7vmin', spin: '-24deg' },
  { left: '31%', top: '13%', color: '#F2707F', size: '0.8vmin', spin: '40deg' },
  { left: '57%', top: '17%', color: '#8ED98A', size: '0.7vmin', spin: '12deg' },
  { left: '74%', top: '26%', color: '#F4B63F', size: '0.8vmin', spin: '-32deg' },
  { left: '90%', top: '41%', color: '#C79BF0', size: '0.8vmin', spin: '26deg' },
  { left: '4%', top: '66%', color: '#6FD3E0', size: '0.7vmin', spin: '-14deg' },
  { left: '15%', top: '85%', color: '#F2707F', size: '0.7vmin', spin: '34deg' },
  { left: '47%', top: '92%', color: '#F4B63F', size: '0.8vmin', spin: '-20deg' },
  { left: '70%', top: '88%', color: '#8ED98A', size: '0.7vmin', spin: '16deg' },
  { left: '86%', top: '73%', color: '#C79BF0', size: '0.8vmin', spin: '-38deg' },
] as const

export function PodiumScreen({ award }: { award: AwardView }) {
  /*
   * Every place gets its own count — 3rd, then 2nd, then 1st, three separate
   * announcements — rather than the card simply appearing when Admin presses
   * the button. The confetti fires as the count ends, from inside the hook.
   */
  const newest = award.results
    .filter((r) => r.revealed && r.revealedAt)
    .sort((a, b) => (b.revealedAt! > a.revealedAt! ? 1 : -1))[0]

  const { pending, count } = useRevealCountdown(
    newest ? `${newest.place}:${newest.revealedAt}` : null,
    'gold',
    newest?.revealedAt ?? null,
    /*
     * The award fanfare, played as the curtain lifts rather than when the
     * server revealed the place — the two are ten seconds apart, and the
     * announcement is the moment the hall can see the name.
     *
     * The same sound for 3rd, 2nd and 1st: the Committee's call, and the
     * right one — the moment belongs to whoever is being announced, not to
     * where they came.
     */
    () => stageSound.awardReveal(),
  )

  /** The place currently counting down, if any. */
  const countingPlace = pending ? Number(pending.split(':')[0]) : null

  return (
    <StageChrome
      palette={PALETTES[award.code] ?? FALLBACK_PALETTE}
      title={award.title}
      subtitle={CEREMONY_SUBTITLE}
      status={
        award.complete
          ? award.places === 1
            ? 'Winner announced'
            : 'All places revealed'
          : `Revealed ${award.revealedCount} of ${award.places}`
      }
      footerLeft={EVENT_FOOTER}
    >
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        {SPECKS.map((speck, index) => (
          <span
            key={index}
            className="absolute block rounded-[0.15vmin]"
            style={{
              left: speck.left,
              top: speck.top,
              width: speck.size,
              height: `calc(${speck.size} * 2)`,
              backgroundColor: speck.color,
              transform: `rotate(${speck.spin})`,
              opacity: 0.85,
            }}
          />
        ))}
      </div>

      {award.screen === 'spotlight' ? (
        <Spotlight award={award} counting={countingPlace === 1} />
      ) : (
        <Podium award={award} countingPlace={countingPlace} />
      )}

      {countingPlace != null && (
        <RevealCurtain
          title={award.title}
          place={award.places === 1 ? 'The winner' : PLACES[countingPlace as 1 | 2 | 3].label}
          count={count}
        />
      )}
    </StageChrome>
  )
}

/**
 * The three places, 2nd left, 1st raised in the middle, 3rd right — slide 26.
 *
 * Each place is a card standing on its own block. The card holds the name; the
 * block holds the number and sets the height, which is what makes the middle
 * read as the top step from the back of the hall.
 */

const PLACES = {
  1: {
    label: '1st place',
    card: 'border-[#E9A52E]',
    block: 'bg-linear-to-b from-[#F6CE63] to-[#E2A52F] text-[#5B4109]',
    badge: 'bg-linear-to-b from-[#F8D269] to-[#E0A22C] text-white',
    ink: 'text-[#B07A12]',
    blockHeight: '21vmin',
    cardWidth: '34vmin',
    icon: Trophy,
  },
  2: {
    label: '2nd place',
    card: 'border-[#AFBACB]',
    block: 'bg-linear-to-b from-[#E4E9F0] to-[#BFC9D6] text-[#4A5467]',
    badge: 'bg-linear-to-b from-[#E8EDF3] to-[#B8C3D1] text-white',
    ink: 'text-[#5B6880]',
    blockHeight: '15vmin',
    cardWidth: '30vmin',
    icon: MedalIcon,
  },
  3: {
    label: '3rd place',
    card: 'border-[#D58A52]',
    block: 'bg-linear-to-b from-[#EFB183] to-[#CE8149] text-[#653413]',
    badge: 'bg-linear-to-b from-[#F0B386] to-[#C97B44] text-white',
    ink: 'text-[#A35A24]',
    blockHeight: '11vmin',
    cardWidth: '30vmin',
    icon: Award,
  },
} as const

function Podium({
  award,
  countingPlace,
}: {
  award: AwardView
  countingPlace: number | null
}) {
  const place = (n: number) => award.results.find((r) => r.place === n)

  // Left to right as the hall sees it, not in rank order.
  return (
    <div className="flex w-full items-end justify-center gap-[2.2vmin]">
      {([2, 1, 3] as const).map((n) => (
        <Step key={n} n={n} result={place(n)} counting={countingPlace === n} />
      ))}
    </div>
  )
}

function Step({
  n,
  result,
  counting,
}: {
  n: 1 | 2 | 3
  result: AwardView['results'][number] | undefined
  counting: boolean
}) {
  const spec = PLACES[n]
  // Held shut until the count finishes, like the attire cards.
  const revealed = (result?.revealed ?? false) && !counting
  const Icon = spec.icon

  return (
    <div className="flex flex-col items-center" style={{ width: spec.cardWidth }}>
      {/*
        The medal sits astride the card's top edge, and drops with it.

        Keyed on the reveal time so the fall plays once, for the place that
        just opened: the stream re-sends the whole ceremony on every payload,
        and without the key all three cards would drop again each time one of
        them was revealed.
      */}
      <div
        key={revealed ? (result?.revealedAt ?? 'open') : 'shut'}
        className={cn('relative w-full', revealed && 'reveal-drop')}
      >
        <div
          className={cn(
            'absolute left-1/2 top-0 z-20 grid -translate-x-1/2 place-items-center rounded-full shadow-[0_4px_14px_rgba(5,30,30,0.35)]',
            counting
              ? 'bg-[#101A33]/80 ring-[0.4vmin] ring-[#FFD16A]/70'
              : revealed
                ? spec.badge
                : 'bg-white/20 text-white/50',
          )}
          style={{ width: n === 1 ? '7.6vmin' : '6.4vmin', height: n === 1 ? '7.6vmin' : '6.4vmin' }}
        >
          <Icon
            className={n === 1 ? 'size-[3.8vmin]' : 'size-[3.1vmin]'}
            strokeWidth={2}
            fill={revealed ? 'currentColor' : 'none'}
            fillOpacity={0.25}
          />
        </div>

        <div
          className={cn(
            'flex w-full flex-col items-center justify-center rounded-[1.6vmin] border-[0.4vmin] px-[1.6vmin] pb-[1.8vmin] text-center',
            n === 1 ? 'pt-[5.4vmin]' : 'pt-[4.6vmin]',
            revealed
              ? cn('bg-white shadow-[0_12px_30px_rgba(5,35,35,0.35)]', spec.card)
              : 'border-white/25 bg-white/10',
          )}
          style={{ marginTop: n === 1 ? '3.8vmin' : '3.2vmin' }}
        >
          <p
            className={cn(
              'text-[1.65vmin] font-bold uppercase tracking-[0.18em]',
              revealed ? spec.ink : 'text-white/45',
            )}
          >
            {spec.label}
          </p>

          {revealed ? (
            /*
             * Keyed on the reveal time so the rise replays for this place and
             * only this place — the stream re-sends the whole ceremony on
             * every payload, and without the key the other two cards would
             * animate again every time one of them opened.
             */
            <div key={result?.revealedAt ?? 'revealed'} className="contents">
              <h2
                className={cn(
                  'mt-[0.7vmin] font-extrabold leading-tight text-[#16255C]',
                  n === 1 ? 'text-[4.2vmin]' : 'text-[3.4vmin]',
                )}
              >
                {result?.label}
              </h2>
              {result?.detail && (
                <p className="mt-[0.5vmin] text-[1.9vmin] leading-snug text-slate-500">
                  {result.detail}
                </p>
              )}
            </div>
          ) : counting ? (
            <p className="mt-[1.6vmin] pb-[0.8vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.18em] text-[#FFD16A]">
              Announcing now
            </p>
          ) : (
            <>
              <span className="mt-[0.9vmin] grid size-[5vmin] place-items-center rounded-full bg-white/10 text-[2.6vmin] font-bold text-white/35">
                ?
              </span>
              <p className="mt-[0.8vmin] text-[1.6vmin] italic text-white/55">not yet revealed</p>
            </>
          )}
        </div>
      </div>

      {/* The step itself. */}
      <div
        className={cn(
          'mt-[1.2vmin] grid w-full place-items-center rounded-[1.2vmin] shadow-[0_8px_20px_rgba(5,35,35,0.3)]',
          revealed ? spec.block : 'bg-white/12 text-white/40',
        )}
        style={{ height: spec.blockHeight }}
      >
        <span
          className={cn('font-extrabold tabular-nums', n === 1 ? 'text-[7vmin]' : 'text-[5.4vmin]')}
        >
          {n}
        </span>
      </div>
    </div>
  )
}

/**
 * One winner, centre stage — Best Cultural Performance.
 *
 * No card: the approved layout puts the winner straight onto the backdrop
 * under a gold laurel, which is what gives a single name the whole screen.
 * The score is shown here (and not on the podium cards) because there is room
 * for it, and because one name with no number invites "how close was it?".
 */
function Spotlight({
  award,
  counting,
}: {
  award: AwardView
  counting: boolean
}) {
  const result = award.results.find((r) => r.place === 1)

  /*
   * Held shut while the curtain counts, exactly as the podium cards are.
   *
   * Without the `!counting` the winner mounted the moment the server revealed
   * it — which is the moment the *count starts* — so the two-second drop
   * played out of sight behind the curtain and the card was already sitting
   * still by the time the hall could see it. The podium had this right and
   * this one did not, which is why only Best Cultural Performance looked
   * wrong.
   */
  const revealed = result?.revealed === true && !counting

  if (!revealed) {
    return (
      <div className="flex flex-col items-center text-center">
        <Laurel>
          <span className="text-[6vmin] font-bold text-[#FFD16A]/40">?</span>
        </Laurel>
        <p className="mt-[2.4vmin] text-[2.6vmin] font-semibold text-white/70">
          {counting ? 'Announcing now' : 'Not yet revealed'}
        </p>
      </div>
    )
  }

  return (
    /* The winner drops in from above, like the podium cards. */
    <div
      key={result.revealedAt ?? 'winner'}
      className="reveal-drop flex flex-col items-center text-center"
    >
      <Laurel>
        <Drama className="size-[9vmin] text-[#FFD16A]" strokeWidth={1.6} />
      </Laurel>

      <span className="mt-[1.6vmin] flex items-center gap-[1.4vmin] rounded-full bg-linear-to-b from-[#F8C456] to-[#E8A42C] px-[4vmin] py-[1.1vmin] text-[2.3vmin] font-extrabold uppercase tracking-[0.2em] text-[#3B2206] shadow-[0_5px_18px_rgba(30,6,30,0.4)]">
        <Star className="size-[1.7vmin] fill-current" strokeWidth={0} />
        Winner
        <Star className="size-[1.7vmin] fill-current" strokeWidth={0} />
      </span>

      <h2 className="mt-[2.4vmin] max-w-[86vw] text-[7.2vmin] font-extrabold leading-none text-white drop-shadow-[0_4px_18px_rgba(25,5,30,0.5)]">
        {result.label}
      </h2>

      <p className="mt-[1.8vmin] text-[2.6vmin] text-white/85">
        {result.displayScore != null ? (
          <>
            Final score {result.displayScore.toFixed(2)} / 100
            <span className="mx-[1.2vmin] text-white/45">·</span>
            average of 3 judges
          </>
        ) : (
          result.detail
        )}
      </p>

      {/*
        The group's own name, when it has one of its own. Suppressed when it
        is the same string as the headline — the seed names the groups
        "Performance Group 3", so the screen printed it twice.
      */}
      {result.detail && result.displayScore != null && result.detail !== result.label && (
        <p className="mt-[0.7vmin] text-[2.2vmin] text-white/65">{result.detail}</p>
      )}

      <p className="mt-[2.2vmin] text-[3.4vmin] font-bold italic text-[#FFD16A]">
        Congratulations!
      </p>
    </div>
  )
}

/**
 * The gold laurel wreath, open at the top.
 *
 * Generated rather than hand-drawn: one leaf rotated nineteen times about the
 * centre, mirrored either side, which keeps it symmetrical and lets the size
 * follow `vmin` like everything else on the stage.
 */
const LEAF_ANGLES = Array.from({ length: 19 }, (_, i) => 26 + i * 17.1)

function Laurel({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative grid size-[21vmin] shrink-0 place-items-center">
      <svg
        viewBox="0 0 100 100"
        className="absolute inset-0 size-full text-[#F2C65C]"
        fill="currentColor"
        aria-hidden
      >
        {LEAF_ANGLES.map((angle) => (
          <g key={angle} transform={`rotate(${angle} 50 50)`}>
            <ellipse
              cx="50"
              cy="12"
              rx="7"
              ry="3.4"
              transform={`rotate(${angle < 180 ? -34 : 34} 50 12)`}
            />
          </g>
        ))}
      </svg>
      {children}
    </div>
  )
}
